import { API_SHIPPING } from "../../config";
import axios from "axios";
import { isAuthenticated } from "./auth";

/**
 * @typedef {Object} OpenCourierService values are still in snake case
 * @property {string} id
 * @property {string} name "Same Day Bike"
 * @property {string} serviceCode "SAME_DAY_001"
 * @property {string} description "Same day delivery for urgent items with motorbike"
 * @property {string[]} collectionMethods ["pickup", "drop_off"]
 * @property {string} duration "3 - 6 hours" - derived from shipmentDurationRange and shipmentDurationUnit
 * @property {string} shipmentDurationRange "3-6"
 * @property {string} shipmentDurationUnit "hour"
 * @property {string} serviceType "same_day", "instant", "overnight", "standard"
 * @property {string} shippingType "parcel", "freight"
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * @typedef {Object} OpenCourier
 * @property {string} courierName
 * @property {string} courierCode
 * @property {string} courierType
 * @property {string|null} getRatesUrl
 * @property {string|null} createShipmentUrl
 * @property {string|null} cancelShipmentUrl
 * @property {string} webhookAuthToken
 * @property {string} clientAuthToken
 * @property {OpenCourierService[]} services
 * @property {string} createdAt
 * @property {string} updatedAt
 */

const serviceFromJSON = (json) => {
  return {
    id: json.id,
    name: json.name,
    serviceCode: json.service_code,
    description: json.description,
    collectionMethods: json.collection_methods,
    duration: json.duration,
    shipmentDurationRange: json.shipment_duration_range,
    shipmentDurationUnit: json.shipment_duration_unit,
    serviceType: json.service_type,
    shippingType: json.shipping_type,
    createdAt: json.created_at,
    updatedAt: json.updated_at,
  }
}

const serviceToJSON = (service) => {
  const payload = {};
  if (service.name !== undefined) payload.name = service.name;
  if (service.serviceCode !== undefined) payload.service_code = service.serviceCode;
  if (service.description !== undefined) payload.description = service.description;
  if (service.collectionMethods !== undefined) payload.collection_methods = service.collectionMethods;
  if (service.shipmentDurationRange !== undefined) payload.shipment_duration_range = service.shipmentDurationRange;
  if (service.shipmentDurationUnit !== undefined) payload.shipment_duration_unit = service.shipmentDurationUnit;
  if (service.serviceType !== undefined) payload.service_type = service.serviceType;
  if (service.shippingType !== undefined) payload.shipping_type = service.shippingType;
  return payload;
}

/**
 * 
 * @param {string} openCourierId 
 * @returns {Promise<OpenCourier>}
 */
export const getOpenCourier = async (openCourierId) => {
  const { token } = isAuthenticated();
  const response = await axios.get(`${API_SHIPPING}/v1/open_couriers/${openCourierId}`, {
    mode: "cors",
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  
  const responseBody = response.data;
  /** @type {OpenCourier} */
  const openCourier = {
    id: responseBody.id,
    courierName: responseBody.courier_name,
    courierCode: responseBody.courier_code,
    courierType: responseBody.courier_type,
    getRatesUrl: responseBody.get_rates_url,
    createShipmentUrl: responseBody.create_shipment_url,
    cancelShipmentUrl: responseBody.cancel_shipment_url,
    webhookAuthToken: responseBody.webhook_auth_token,
    clientAuthToken: responseBody.client_auth_token,
    services: responseBody.services.map(serviceFromJSON),
    createdAt: responseBody.created_at,
    updatedAt: responseBody.updated_at,
  };

  return openCourier;
};

/**
 * 
 * @param {string} openCourierId 
 * @param {Object} data 
 * @param {string} [data.getRatesUrl]
 * @param {string} [data.createShipmentUrl]
 * @param {string} [data.cancelShipmentUrl]
 * @returns {Promise<OpenCourier>}
 */
export const updateOpenCourier = async (openCourierId, data) => {
  const { token } = isAuthenticated();
  
  const payload = {};
  if (data.getRatesUrl) {
    payload.get_rates_url = data.getRatesUrl;
  }
  if (data.createShipmentUrl) {
    payload.create_shipment_url = data.createShipmentUrl;
  }
  if (data.cancelShipmentUrl) {
    payload.cancel_shipment_url = data.cancelShipmentUrl;
  }

  const response = await axios.post(`${API_SHIPPING}/v1/open_couriers/${openCourierId}`, payload, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  const responseBody = response.data;
  /** @type {OpenCourier} */
  const openCourier = {
    id: responseBody.id,
    courierName: responseBody.courier_name,
    courierCode: responseBody.courier_code,
    courierType: responseBody.courier_type,
    getRatesUrl: responseBody.get_rates_url,
    createShipmentUrl: responseBody.create_shipment_url,
    cancelShipmentUrl: responseBody.cancel_shipment_url,
    webhookAuthToken: responseBody.webhook_auth_token,
    clientAuthToken: responseBody.client_auth_token,
    createdAt: responseBody.created_at,
    updatedAt: responseBody.updated_at,
  };

  return openCourier;
};

/**
 * 
 * @param {string} openCourierId 
 * @returns {Promise<string>}
 */
export const generateWebhookAuthToken = async (openCourierId) => {
  const { token } = isAuthenticated();
  const response = await axios.post(`${API_SHIPPING}/v1/open_couriers/${openCourierId}/webhook_auth_token`, {}, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data.webhook_auth_token;
};

/**
 * 
 * @param {string} openCourierId 
 * @returns {Promise<string>}
 */
export const generateClientAuthToken = async (openCourierId) => {
  const { token } = isAuthenticated();
  const response = await axios.post(`${API_SHIPPING}/v1/open_couriers/${openCourierId}/client_auth_token`, {}, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data.client_auth_token;
};

/**
 * Update get rates url and try to make an API call to the url
 * @param {string} openCourierId 
 * @param {string} getRatesUrl
 */
export const updateGetRatesUrl = async (openCourierId, getRatesUrl) => {
  const { token } = isAuthenticated();
  const response = await axios.post(`${API_SHIPPING}/v1/open_couriers/${openCourierId}/get_rates_url`, {
    get_rates_url: getRatesUrl,
  }, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  const responseBody = response.data;
  const formattedResponse = {
    id: responseBody.id,
    getRatesUrl: responseBody.get_rates_url,
    webhookResult: {
      requestBody: responseBody.webhook_result.request_body,
      responseBody: responseBody.webhook_result.response_body,
      httpStatusCode: responseBody.webhook_result.http_status_code,
      responseTime: responseBody.webhook_result.response_time,
      responseTimeUnit: responseBody.webhook_result.response_time_unit,
      httpMethod: responseBody.webhook_result.http_method,
    },
  };

  return formattedResponse;
};

/**
 * Update create shipment url and try to make an API call to the url
 * @param {string} openCourierId 
 * @param {string} createShipmentUrl
 */
export const updateCreateShipmentUrl = async (openCourierId, createShipmentUrl) => {
  const { token } = isAuthenticated();
  const response = await axios.post(`${API_SHIPPING}/v1/open_couriers/${openCourierId}/create_shipment_url`, {
    create_shipment_url: createShipmentUrl,
  }, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  const responseBody = response.data;
  const formattedResponse = {
    id: responseBody.id,
    createShipmentUrl: responseBody.create_shipment_url,
    webhookResult: {
      requestBody: responseBody.webhook_result.request_body,
      responseBody: responseBody.webhook_result.response_body,
      httpStatusCode: responseBody.webhook_result.http_status_code,
      responseTime: responseBody.webhook_result.response_time,
      responseTimeUnit: responseBody.webhook_result.response_time_unit,
      httpMethod: responseBody.webhook_result.http_method,
    },
  };

  return formattedResponse;
};  

/**
 * Update cancel shipment url and try to make an API call to the url
 * @param {string} openCourierId 
 * @param {string} cancelShipmentUrl
 */
export const updateCancelShipmentUrl = async (openCourierId, cancelShipmentUrl) => {
  const { token } = isAuthenticated();
  const response = await axios.post(`${API_SHIPPING}/v1/open_couriers/${openCourierId}/cancel_shipment_url`, {
    cancel_shipment_url: cancelShipmentUrl,
  }, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  const responseBody = response.data;
  const formattedResponse = {
    id: responseBody.id,
    cancelShipmentUrl: responseBody.cancel_shipment_url,
    webhookResult: {
      requestBody: responseBody.webhook_result.request_body,
      responseBody: responseBody.webhook_result.response_body,
      httpStatusCode: responseBody.webhook_result.http_status_code,
      responseTime: responseBody.webhook_result.response_time,
      responseTimeUnit: responseBody.webhook_result.response_time_unit,
      httpMethod: responseBody.webhook_result.http_method,
    },
  };

  return formattedResponse;
};

/**
 * Create a new service for an Open Courier
 * @param {string} openCourierId 
 * @param {Object} service - Service data in camelCase
 * @param {string} service.name
 * @param {string} service.serviceCode
 * @param {string} service.description
 * @param {string[]} service.collectionMethods
 * @param {string} service.shipmentDurationRange
 * @param {string} service.shipmentDurationUnit
 * @param {string} service.serviceType
 * @param {string} service.shippingType
 * @returns {Promise<OpenCourierService>}
 */
export const createOpenCourierService = async (openCourierId, service) => {
  const { token } = isAuthenticated();
  const payload = serviceToJSON(service);

  const response = await axios.post(
    `${API_SHIPPING}/v1/open_couriers/${openCourierId}/services`,
    payload,
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return serviceFromJSON(response.data);
};

/**
 * Update an existing service for an Open Courier
 * @param {string} openCourierId 
 * @param {string} serviceId
 * @param {Object} service - Service data in camelCase (all fields optional)
 * @returns {Promise<OpenCourierService>}
 */
export const updateOpenCourierService = async (openCourierId, serviceId, service) => {
  const { token } = isAuthenticated();
  const payload = serviceToJSON(service);

  const response = await axios.post(
    `${API_SHIPPING}/v1/open_couriers/${openCourierId}/services/${serviceId}`,
    payload,
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return serviceFromJSON(response.data);
};

/**
 * Delete a service from an Open Courier
 * @param {string} openCourierId 
 * @param {string} serviceId
 * @returns {Promise<{success: boolean, id: string}>}
 */
export const deleteOpenCourierService = async (openCourierId, serviceId) => {
  const { token } = isAuthenticated();

  const response = await axios.delete(
    `${API_SHIPPING}/v1/open_couriers/${openCourierId}/services/${serviceId}`,
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return {
    success: response.data.success,
    id: response.data.id,
  };
};

